import {
    Component,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import Chart from 'chart.js/auto';
import {
    DashboardKeyMetricsService,
    EnvironmentalTimeSeriesResponse,
    EnvironmentalTimeSeriesRequest
} from '@services/dashboard-key-metrics.service';
import {FarmService} from '@services/farm.service';
import {Subject, takeUntil} from 'rxjs';

type SensorType = 'Temperature' | 'Humidity' | 'CO2';
const HOUSE_ZERO_ID = 1;
interface House {
    id: number;
    name: string;
}

@Component({
    selector: 'app-environmental-time-series-graph',
    standalone: true,
    imports: [CommonModule, FormsModule],
    templateUrl: './environmental-time-series-graph.component.html',
    styleUrl: './environmental-time-series-graph.component.scss'
})
export class EnvironmentalTimeSeriesGraphComponent
    implements OnInit, OnChanges, OnDestroy
{
    @Input() farmId: number | null = null;

    // Filter state
    selectedHouseId: number | null = null;
    selectedSensorType: SensorType = 'Temperature';
    startDate: string = '';
    endDate: string = '';

    // Data
    houses: House[] = [];
    isLoading = false;
    errorMessage = '';

    // Sensor type tabs
    sensorTypes: {id: SensorType; label: string; unit: string}[] = [
        {id: 'Temperature', label: 'Temperature', unit: '°C'},
        {id: 'Humidity', label: 'Humidity', unit: '%'},
        {id: 'CO2', label: 'CO₂', unit: 'ppm'}
    ];

    private chart: Chart | null = null;
    private destroy$ = new Subject<void>();

    constructor(
        private dashboardService: DashboardKeyMetricsService,
        private farmService: FarmService
    ) {}

    ngOnInit(): void {
        this.initializeDateRange();
        if (this.farmId) {
            this.loadHouses();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['farmId'] && !changes['farmId'].firstChange) {
            this.loadHouses();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.chart?.destroy();
    }

    private initializeDateRange(): void {
        const now = new Date();
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);

        this.endDate = this.formatDateTimeLocal(now);
        this.startDate = this.formatDateTimeLocal(yesterday);
    }

    private formatDateTimeLocal(date: Date): string {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
    }

    private loadHouses(): void {
        if (!this.farmId) return;

        this.farmService
            .getFarmHouseList(this.farmId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (houses) => {
                    this.houses = houses
                        .filter((h: any) => h.id !== HOUSE_ZERO_ID)
                        .map((h: any) => ({
                            id: h.id,
                            name: h.name
                        }));

                    if (this.houses.length > 0 && !this.selectedHouseId) {
                        this.selectedHouseId = this.houses[0].id;
                        this.fetchData();
                    }
                },
                error: (err) => {
                    console.error('Error loading houses:', err);
                    this.houses = [];
                }
            });
    }

    onHouseChange(): void {
        this.fetchData();
    }

    onSensorTypeChange(sensorType: SensorType): void {
        this.selectedSensorType = sensorType;
        this.fetchData();
    }

    onDateChange(): void {
        this.fetchData();
    }

    fetchData(): void {
        if (
            !this.farmId ||
            !this.selectedHouseId ||
            !this.startDate ||
            !this.endDate
        ) {
            return;
        }

        this.isLoading = true;
        this.errorMessage = '';

        const request: EnvironmentalTimeSeriesRequest = {
            farmId: this.farmId,
            houseId: this.selectedHouseId,
            sensorType: this.selectedSensorType,
            startDate: new Date(this.startDate).toISOString(),
            endDate: new Date(this.endDate).toISOString()
        };

        this.dashboardService
            .getEnvironmentalTimeSeries(request)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (data) => {
                    this.isLoading = false;
                    this.renderChart(data);
                },
                error: (err) => {
                    this.isLoading = false;
                    this.errorMessage =
                        'Failed to load data. Please try again.';
                    console.error(
                        'Error fetching environmental time series:',
                        err
                    );
                    this.renderEmptyChart();
                }
            });
    }

    private getUnit(): string {
        return (
            this.sensorTypes.find((s) => s.id === this.selectedSensorType)
                ?.unit || ''
        );
    }

    private renderChart(data: EnvironmentalTimeSeriesResponse): void {
        const ctx = document.getElementById(
            'environmentalTimeSeriesChart'
        ) as HTMLCanvasElement;
        if (!ctx) return;

        // Destroy existing chart
        if (this.chart) {
            this.chart.destroy();
        }

        const actualData = data.actual || [];
        const predictedData = data.predicted || [];

        // Format labels as readable timestamps
        const allTimestamps = [
            ...actualData.map((d) => d.timestamp),
            ...predictedData.map((d) => d.timestamp)
        ];
        const uniqueTimestamps = [...new Set(allTimestamps)].sort();

        const labels = uniqueTimestamps.map((ts) => {
            const date = new Date(ts);
            return date.toLocaleString('en-SA', {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        });

        // Map data to timestamps
        const actualValues = uniqueTimestamps.map((ts) => {
            const point = actualData.find((d) => d.timestamp === ts);
            return point ? point.value : null;
        });

        const predictedValues = uniqueTimestamps.map((ts) => {
            const point = predictedData.find((d) => d.timestamp === ts);
            return point ? point.value : null;
        });

        const unit = this.getUnit();

        this.chart = new Chart(ctx, {
            type: 'line',
            data: {
                labels,
                datasets: [
                    {
                        label: `Actual ${this.selectedSensorType}`,
                        data: actualValues,
                        borderColor: 'rgba(83, 255, 240, 1)',
                        backgroundColor: 'rgba(83, 255, 240, 0.1)',
                        borderWidth: 2,
                        pointBackgroundColor: 'rgba(83, 255, 240, 1)',
                        pointRadius: 3,
                        pointHoverRadius: 5,
                        fill: false,
                        tension: 0.3,
                        spanGaps: true
                    },
                    {
                        label: `Predicted ${this.selectedSensorType}`,
                        data: predictedValues,
                        borderColor: 'rgba(254, 195, 9, 1)',
                        backgroundColor: 'rgba(254, 195, 9, 0.1)',
                        borderWidth: 2,
                        borderDash: [5, 5],
                        pointBackgroundColor: 'rgba(254, 195, 9, 1)',
                        pointRadius: 3,
                        pointHoverRadius: 5,
                        fill: false,
                        tension: 0.3,
                        spanGaps: true
                    }
                ]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: {
                    intersect: false,
                    mode: 'index'
                },
                plugins: {
                    title: {
                        display: true,
                        text: `${this.selectedSensorType} - Actual vs Predicted`,
                        color: '#fff',
                        font: {size: 16, weight: 'bold'},
                        padding: {top: 10, bottom: 20}
                    },
                    legend: {
                        display: true,
                        position: 'bottom',
                        labels: {
                            color: '#fff',
                            font: {size: 12},
                            usePointStyle: true,
                            padding: 20
                        }
                    },
                    tooltip: {
                        backgroundColor: 'rgba(0, 30, 27, 0.95)',
                        titleColor: '#fff',
                        bodyColor: '#fff',
                        borderColor: 'rgba(83, 255, 240, 0.3)',
                        borderWidth: 1,
                        callbacks: {
                            label: (ctx) => {
                                const value = ctx.parsed.y;
                                if (value === null) return '';
                                return `${ctx.dataset.label}: ${value.toFixed(2)} ${unit}`;
                            }
                        }
                    }
                },
                scales: {
                    x: {
                        title: {
                            display: true,
                            text: 'Timestamp',
                            color: '#fff',
                            font: {size: 12}
                        },
                        ticks: {
                            color: '#fff',
                            maxRotation: 45,
                            minRotation: 45,
                            maxTicksLimit: 12
                        },
                        grid: {
                            color: 'rgba(255, 255, 255, 0.1)'
                        }
                    },
                    y: {
                        title: {
                            display: true,
                            text: `${this.selectedSensorType} (${unit})`,
                            color: '#fff',
                            font: {size: 12}
                        },
                        ticks: {
                            color: '#fff',
                            callback: (value) => `${value} ${unit}`
                        },
                        grid: {
                            color: 'rgba(255, 255, 255, 0.1)'
                        }
                    }
                }
            }
        });
    }

    private renderEmptyChart(): void {
        const ctx = document.getElementById(
            'environmentalTimeSeriesChart'
        ) as HTMLCanvasElement;
        if (!ctx) return;

        if (this.chart) {
            this.chart.destroy();
        }

        this.chart = new Chart(ctx, {
            type: 'line',
            data: {
                labels: [],
                datasets: []
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    title: {
                        display: true,
                        text: 'No data available',
                        color: '#fff',
                        font: {size: 16}
                    }
                }
            }
        });
    }
}
